import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Banknote } from 'lucide-react';
import { subFinancialsApi } from '../api';
import { centsToDollarString } from '../constants';
import { balanceLabel } from '../subConstants';
import { CommitmentsView } from './CommitmentsView';
import { SubPaymentsList } from './SubPaymentsList';
import { SubPaymentFormDialog } from './SubPaymentFormDialog';

const StatCard = ({ label, value, sub, testId, accent }) => (
  <Card className="bg-[#111111] border-gray-800 p-4" data-testid={testId}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-xl font-bold mt-1 ${accent || 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-600 mt-0.5">{sub}</div>}
  </Card>
);

export const ProjectSubcontractorsTab = ({ project }) => {
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(() => {
    subFinancialsApi.summary({ project_id: project.id }).then((r) => setSummary(r.data)).catch(() => {});
    subFinancialsApi.balances({ project_id: project.id, group_by: 'subcontractor' }).then((r) => setBreakdown(r.data)).catch(() => {});
  }, [project.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const bump = () => setRefreshKey((k) => k + 1);
  const bal = summary?.known_balance_cents;

  return (
    <div data-testid="project-subcontractors-tab">
      <p className="text-xs text-gray-500 mb-4">
        Counted subcontractor cost comes only from approved and completed commitments.
        Payments reduce the remaining balance — they never add to project cost.
      </p>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatCard label="Recorded Subcontractor Commitments" value={centsToDollarString(summary.total_counted_commitment_cents)} sub="Approved + completed only" testId="psub-counted" />
          <StatCard label="Recorded Payments" value={centsToDollarString(summary.recorded_payments_cents)} testId="psub-paid" />
          <StatCard
            label="Known Remaining Balance"
            value={centsToDollarString(bal)}
            accent={bal > 0 ? 'text-[#D4AF37]' : bal < 0 ? 'text-red-400' : 'text-white'}
            sub={bal < 0 ? 'Advance / Overpayment' : null}
            testId="psub-balance"
          />
          <StatCard label="Draft Commitments" value={String(summary.draft_commitment_count)} sub="Not counted until approved" testId="psub-drafts" />
        </div>
      )}

      {project.is_archived && (
        <div className="text-sm text-gray-400 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 mb-4" data-testid="psub-archived-note">
          This Project is archived. Existing commitments and payments are preserved. New commitments are blocked,
          but payments can still be recorded for outstanding obligations.
        </div>
      )}

      {breakdown.length > 0 && (
        <Card className="bg-[#111111] border-gray-800 p-5 mb-5" data-testid="psub-breakdown">
          <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">By Subcontractor</h4>
          <div className="space-y-1">
            {breakdown.map((b) => (
              <div key={b.subcontractor_id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-gray-800/60 last:border-0" data-testid={`psub-row-${b.subcontractor_id}`}>
                <div className="min-w-0">
                  <span className="text-sm text-gray-200">{b.subcontractor?.display_name}</span>
                  <span className="text-xs text-gray-500 ml-2">{b.commitment_count} commitment{b.commitment_count === 1 ? '' : 's'}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-400">Committed <span className="text-gray-200">{centsToDollarString(b.counted_commitment_cents)}</span></span>
                  <span className="text-gray-400">Paid <span className="text-gray-200">{centsToDollarString(b.recorded_payments_cents)}</span></span>
                  <span className={`font-semibold ${b.known_balance_cents > 0 ? 'text-[#D4AF37]' : b.known_balance_cents < 0 ? 'text-red-400' : 'text-gray-200'}`} title={balanceLabel(b.known_balance_cents)}>
                    {centsToDollarString(b.known_balance_cents)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mb-8">
        <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide mb-3">Commitments</h4>
        <CommitmentsView lockedProjectId={project.id} disableAdd={project.is_archived} refreshKey={refreshKey} onChanged={bump} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Payments</h4>
          <Button onClick={() => setPayOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="psub-record-payment-btn">
            <Banknote size={16} /> Record Payment
          </Button>
        </div>
        <SubPaymentsList lockedProjectId={project.id} refreshKey={refreshKey} onChanged={bump} />
      </div>

      <SubPaymentFormDialog open={payOpen} onOpenChange={setPayOpen} lockedProjectId={project.id} onSaved={bump} />
    </div>
  );
};
